import type { ReactNode } from 'react';
import { SombrasDaFaixa, classeFaixaRolavel, useFaixaRolavel } from './FaixaRolavel.js';

export interface Aba<T extends string> {
  valor: T;
  rotulo: ReactNode;
  contagem?: number;
}

/**
 * Abas com sublinhado: o filtro institucional, no lugar das pilulas. A aba
 * ativa ganha o filete de 2px e o texto na cor da marca.
 *
 * No celular as abas que nao cabem rolam de lado. Um degrade na borda avisa que
 * ha mais abas, e a ativa rola para a vista (inclusive a escolhida por link).
 */
export function Abas<T extends string>({
  abas,
  valor,
  onMudar,
  rotulo,
  tamanho = 'md',
}: {
  abas: readonly Aba<T>[];
  valor: T;
  onMudar: (v: T) => void;
  rotulo: string;
  tamanho?: 'md' | 'sm';
}) {
  const { ref: lista, resta } = useFaixaRolavel<HTMLDivElement>(valor, '[aria-selected="true"]');

  return (
    <div className="relative -mx-4 sm:mx-0">
      <div
        ref={lista}
        role="tablist"
        aria-label={rotulo}
        className={`${classeFaixaRolavel} border-b border-borda px-4 sm:px-0`}
      >
        {abas.map((a) => {
          const ativa = a.valor === valor;
          return (
            <button
              key={a.valor}
              type="button"
              role="tab"
              aria-selected={ativa}
              onClick={() => onMudar(a.valor)}
              className={`relative -mb-px flex min-h-11 shrink-0 items-center gap-2 border-b-2 px-3 font-medium select-none transition-colors ${
                tamanho === 'sm' ? 'text-[13px]' : 'text-sm'
              } ${ativa ? 'border-marca text-marca' : 'border-transparent text-apagado hover:border-borda-forte hover:text-texto'}`}
            >
              {a.rotulo}
              {a.contagem != null && (
                <span className={`numero text-xs ${ativa ? 'text-marca' : 'text-apagado'}`}>{a.contagem}</span>
              )}
            </button>
          );
        })}
      </div>
      <SombrasDaFaixa resta={resta} />
    </div>
  );
}

/**
 * Alternador compacto de 2-5 opcoes (ex.: Lista | Cartoes, periodo): controle
 * segmentado, a opcao ativa e um bloco cheio dentro da moldura.
 */
export function Alternador<T extends string>({
  opcoes,
  valor,
  onMudar,
  rotulo,
}: {
  opcoes: readonly { valor: T; rotulo: ReactNode; titulo: string }[];
  valor: T;
  onMudar: (v: T) => void;
  rotulo: string;
}) {
  return (
    <div
      role="radiogroup"
      aria-label={rotulo}
      className="inline-flex flex-wrap gap-0.5 rounded-(--radius-controle) border border-borda bg-superficie p-0.5"
    >
      {opcoes.map((o) => {
        const ativo = o.valor === valor;
        return (
          <button
            key={o.valor}
            type="button"
            role="radio"
            aria-checked={ativo}
            // Com texto visivel, ele e o nome acessivel (comando de voz diz o que ve); so icone usa o titulo.
            aria-label={typeof o.rotulo === 'string' ? undefined : o.titulo}
            title={o.titulo}
            onClick={() => onMudar(o.valor)}
            className={`flex items-center gap-1.5 rounded-[6px] px-3 text-[13px] font-medium transition-colors ${
              ativo ? 'bg-marca text-sobre-marca' : 'text-apagado hover:bg-superficie-2 hover:text-texto'
            }`}
          >
            {o.rotulo}
          </button>
        );
      })}
    </div>
  );
}
